import { focusControl } from "./controls.js";
type AiRopRole = "manager" | "director" | "admin";
type AiRopView = "events" | "dashboard";

type AiRopOverview = {
  reportId: string;
  generatedAt: string;
  scope?: AiRopScope;
  dataQuality?: Record<string, unknown>;
  summary: Record<string, unknown>;
  byViolationType: Array<{ code: string; count: number }>;
  managerRating: Array<{ manager: string; managerId?: number; total: number; critical: number; high: number; medium: number }>;
};

type AiRopScope = {
  pipelineMode?: string;
  mode?: string;
  configuredPipelineIds?: Array<number | string>;
  activePipelineIds?: Array<number | string>;
  missingPipelineIds?: Array<number | string>;
  unsortedPipelineIds?: Array<number | string>;
  closedDealsExcluded?: number | null;
};

type AiRopViolation = {
  stableKey: string;
  code: string;
  title: string;
  criticality: "critical" | "high" | "medium";
  status: string;
  dealId: number;
  dealName: string;
  dealUrl: string;
  responsibleName: string;
  responsibleId?: number;
  pipelineId?: number | string;
  pipelineName: string;
  stageId?: number | string;
  stageName: string;
  evidence: string;
  recommendation: string;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  ageDays?: number;
};

type AiFilterOption = { id: number | string | null; name: string };
type AiEventType = { code: string; title: string };
type AiStageOption = AiFilterOption & { pipelineId: number | string | null; pipelineName?: string };

type AiRopFilters = {
  managers: AiFilterOption[];
  eventTypes: AiEventType[];
  pipelines: AiFilterOption[];
  stages: AiStageOption[];
  criticalities: Array<string | { id?: string; value?: string; name?: string; label?: string }>;
  statuses: Array<string | { id?: string; value?: string; name?: string; label?: string }>;
  dateRange?: { from?: string; to?: string };
};

type AnalyticsRow = Record<string, unknown> & {
  id?: number | string;
  name?: string;
  code?: string;
  title?: string;
  pipelineId?: number | string;
  pipelineName?: string;
};

type AiRopAnalytics = {
  generatedAt: string | null;
  period?: { from?: string; to?: string; days?: number; canonicalReports?: number };
  summary: Record<string, unknown>;
  series: Array<Record<string, unknown> & { date?: string }>;
  byType: AnalyticsRow[];
  byPipelineStage: { pipelines?: AnalyticsRow[]; stages?: AnalyticsRow[] };
  employees: AnalyticsRow[];
  aging: AnalyticsRow[];
  dataQuality?: Record<string, unknown>;
};

type FocusRestore = { filter: string; position?: number | null };

const numberFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 });
const integerFormatter = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const defaultFilters: AiRopFilters = {
  managers: [],
  eventTypes: [],
  pipelines: [],
  stages: [],
  criticalities: ["critical", "high", "medium"],
  statuses: ["new", "open", "reopened", "acknowledged", "ignored", "fixed"]
};

let workspaceController: AbortController | null = null;
let workspaceRequestId = 0;
let searchTimer: number | undefined;
let runRefreshTimer: number | undefined;
let requestedTabFocus: AiRopView | null = null;

class PortalSessionExpiredError extends Error {}

export function aiRopPage() {
  return `
    <section class="ai-rop-workspace" id="ai-rop-workspace" aria-busy="true">
      ${renderLoadingState()}
    </section>
  `;
}

export async function hydrateAiRop(role: AiRopRole, restoreFocus?: FocusRestore) {
  const host = document.querySelector<HTMLElement>("#ai-rop-workspace");
  if (!host) return;
  workspaceController?.abort();
  const controller = new AbortController();
  workspaceController = controller;
  const requestId = ++workspaceRequestId;
  const params = workspaceParams();
  const view = currentView(params);
  host.setAttribute("aria-busy", "true");
  host.classList.add("is-updating");

  try {
    const filtersQuery = queryForFilters(params, role);
    const filtersPromise = request<AiRopFilters>(`/api/ai-rop/filters${querySuffix(filtersQuery)}`, { signal: controller.signal });

    if (view === "dashboard") {
      const analyticsQuery = queryForData(params, "dashboard");
      const [filters, analytics] = await Promise.all([
        filtersPromise,
        request<AiRopAnalytics>(`/api/ai-rop/analytics${querySuffix(analyticsQuery)}`, { signal: controller.signal })
      ]);
      if (controller.signal.aborted || requestId !== workspaceRequestId) return;
      host.innerHTML = renderWorkspaceShell(view, role, analytics.generatedAt, filters, params, renderAnalytics(analytics, params));
    } else {
      const eventsQuery = queryForData(params, "events");
      const overviewQuery = new URLSearchParams(eventsQuery);
      overviewQuery.delete("q");
      overviewQuery.delete("sort");
      overviewQuery.delete("limit");
      overviewQuery.delete("offset");
      const [filters, overview, violations] = await Promise.all([
        filtersPromise,
        request<AiRopOverview>(`/api/ai-rop/overview${querySuffix(overviewQuery)}`, { signal: controller.signal }),
        request<{ items: AiRopViolation[]; total: number }>(`/api/ai-rop/violations${querySuffix(eventsQuery)}`, { signal: controller.signal })
      ]);
      if (controller.signal.aborted || requestId !== workspaceRequestId) return;
      host.innerHTML = renderWorkspaceShell(view, role, overview.generatedAt, filters, params, renderEvents(overview, violations.items, violations.total, role, params));
      updateCriticalBadges(numberValue(overview.summary.criticalViolations) ?? 0);
    }

    host.removeAttribute("aria-busy");
    host.classList.remove("is-updating");
    bindWorkspace(role);
    restoreFilterFocus(restoreFocus);
    if (requestedTabFocus) {
      document.querySelector<HTMLButtonElement>(`[data-ai-tab="${requestedTabFocus}"]`)?.focus();
      requestedTabFocus = null;
    }
  } catch (error) {
    if (isAbortError(error) || controller.signal.aborted || requestId !== workspaceRequestId) return;
    if (error instanceof PortalSessionExpiredError) return;
    host.innerHTML = renderErrorState(error);
    host.removeAttribute("aria-busy");
    host.classList.remove("is-updating");
    host.querySelector("[data-ai-rop-retry]")?.addEventListener("click", () => void hydrateAiRop(role));
  }
}

export async function refreshAiRopCriticalCount() {
  try {
    const overview = await request<AiRopOverview>("/api/ai-rop/overview");
    const count = numberValue(overview.summary.criticalViolations) ?? 0;
    updateCriticalBadges(count);
    return count;
  } catch {
    updateCriticalBadges(0);
    return 0;
  }
}

function renderWorkspaceShell(
  view: AiRopView,
  role: AiRopRole,
  generatedAt: string | null | undefined,
  filters: AiRopFilters,
  params: URLSearchParams,
  body: string
) {
  return `
    <section class="ai-rop-page" data-ai-view="${view}">
      <header class="ai-rop-page-header">
        <div class="ai-rop-tabs" role="tablist" aria-label="Разделы ИИ-РОПа">
          ${viewTab("events", "События", view)}
          ${viewTab("dashboard", "Дашборд", view)}
        </div>
        <div class="ai-rop-page-actions">
          <div class="ai-rop-freshness"><span>Данные на</span><strong>${formatDateTime(generatedAt)}</strong></div>
          ${role === "manager" ? "" : renderRunAction()}
        </div>
      </header>
      ${renderFilterBar(filters || defaultFilters, params, role, view)}
      <div class="ai-rop-view-content">${body}</div>
      <p class="sr-only" aria-live="polite" data-ai-live></p>
    </section>
  `;
}

function viewTab(value: AiRopView, label: string, active: AiRopView) {
  const selected = value === active;
  return `<button type="button" role="tab" aria-selected="${selected}" tabindex="${selected ? "0" : "-1"}" data-ai-tab="${value}" class="${selected ? "is-active" : ""}">${label}</button>`;
}

function renderRunAction() {
  return `
    <span class="ai-run-action">
      <button class="primary" type="button" data-ai-run aria-describedby="ai-run-tooltip">Запустить проверку</button>
      <span class="ai-tooltip" id="ai-run-tooltip" role="tooltip" data-ai-tooltip>Запускает внеплановую проверку актуальных сделок в amoCRM. Результат появится после завершения анализа.</span>
    </span>
  `;
}

function renderFilterBar(filters: AiRopFilters, params: URLSearchParams, role: AiRopRole, view: AiRopView) {
  const normalized = normalizeFilters(filters);
  const pipelineId = params.get("pipelineId") || "";
  const stages = normalized.stages.filter((stage) => !pipelineId || filterOptionValue(stage.pipelineId, stage.pipelineName || "") === pipelineId);
  const eventOptions = normalized.eventTypes.map((item) => [String(item.code), item.title] as [string, string]);
  const pipelineOptions = normalized.pipelines.map((item) => [filterOptionValue(item.id, item.name), item.name] as [string, string]);
  const stageOptions = stages.map((item) => [filterOptionValue(item.id, item.name), pipelineId ? item.name : `${item.pipelineName ? `${item.pipelineName} · ` : ""}${item.name}`] as [string, string]);
  const managerOptions = normalized.managers.filter((item) => item.id !== null).map((item) => [String(item.id), item.name] as [string, string]);
  const criticalityOptions = normalizeValueOptions(normalized.criticalities, severityLabel);
  const statusOptions = normalizeValueOptions(normalized.statuses, statusLabel);
  const chipOptions = { eventOptions, pipelineOptions, stageOptions, managerOptions, criticalityOptions, statusOptions };

  return `
    <section class="ai-filter-panel" aria-label="Фильтры данных ИИ-РОПа" data-ai-date-from="${escapeHtml(normalized.dateRange?.from || "")}" data-ai-date-to="${escapeHtml(normalized.dateRange?.to || "")}">
      <div class="ai-filter-grid">
        ${view === "events" ? filterText("q", "Поиск", params.get("q") || "", "Название или номер сделки") : ""}
        ${role === "manager" ? "" : filterSelect("managerId", "Сотрудник", params.get("managerId") || "", [["", "Все сотрудники"], ...managerOptions])}
        ${filterSelect("code", "Причина нарушения", params.get("code") || "", [["", "Все причины"], ...eventOptions])}
        ${filterSelect("criticality", "Критичность", params.get("criticality") || "", [["", "Любая"], ...criticalityOptions])}
        ${filterSelect("status", "Состояние", params.get("status") || "", [["", "Все активные"], ...statusOptions])}
        ${filterSelect("pipelineId", "Воронка", pipelineId, [["", "Все воронки"], ...pipelineOptions])}
        ${filterSelect("stageId", "Этап", params.get("stageId") || "", [["", pipelineId ? "Все этапы воронки" : "Все этапы"], ...stageOptions], !stages.length)}
        ${filterSelect("period", "Период", params.get("period") || "30d", periodOptions())}
        ${params.get("period") === "custom" ? filterDate("from", "Дата начала", params.get("from") || normalized.dateRange?.from || "", normalized.dateRange) : ""}
        ${params.get("period") === "custom" ? filterDate("to", "Дата окончания", params.get("to") || normalized.dateRange?.to || "", normalized.dateRange) : ""}
        ${filterSelect("age", "Возраст события", params.get("age") || "", ageOptions())}
        ${view === "events" ? filterSelect("sort", "Сортировка", params.get("sort") || "criticality:desc", sortOptions()) : ""}
        <label class="ai-filter-toggle">
          <input type="checkbox" data-ai-filter="includeFixed" ${params.get("includeFixed") === "true" ? "checked" : ""} />
          <span>Показывать исправленные</span>
        </label>
      </div>
      ${renderActiveFilters(params, view, chipOptions)}
    </section>
  `;
}

function filterText(key: string, label: string, value: string, placeholder: string) {
  return `
    <label class="ai-filter ai-filter-search">
      <span>${escapeHtml(label)}</span>
      <input type="search" value="${escapeHtml(value)}" placeholder="${escapeHtml(placeholder)}" autocomplete="off" data-ai-filter="${key}" />
    </label>
  `;
}

function filterSelect(key: string, label: string, value: string, values: Array<[string, string]>, disabled = false) {
  return `
    <label class="ai-filter">
      <span>${escapeHtml(label)}</span>
      <select data-ai-filter="${key}" ${disabled ? "disabled" : ""}>${selectOptions(value, values)}</select>
    </label>
  `;
}

function filterDate(key: "from" | "to", label: string, value: string, range?: { from?: string; to?: string }) {
  return `
    <label class="ai-filter ai-filter-date">
      <span>${escapeHtml(label)}</span>
      <input type="date" value="${escapeHtml(value)}" min="${escapeHtml(range?.from || "")}" max="${escapeHtml(range?.to || "")}" data-ai-filter="${key}" />
    </label>
  `;
}

function renderActiveFilters(
  params: URLSearchParams,
  view: AiRopView,
  options: Record<string, Array<[string, string]>>
) {
  const definitions: Array<[string, string, Array<[string, string]>?]> = [
    ["q", "Поиск"],
    ["managerId", "Сотрудник", options.managerOptions],
    ["code", "Причина нарушения", options.eventOptions],
    ["criticality", "Критичность", options.criticalityOptions],
    ["status", "Состояние", options.statusOptions],
    ["pipelineId", "Воронка", options.pipelineOptions],
    ["stageId", "Этап", options.stageOptions],
    ["period", "Период", periodOptions()],
    ["from", "С", undefined],
    ["to", "По", undefined],
    ["age", "Возраст", ageOptions()],
    ["sort", "Сортировка", sortOptions()]
  ];
  const chips = definitions.flatMap(([key, label, values]) => {
    if ((key === "q" || key === "sort") && view !== "events") return [];
    const value = params.get(key) || "";
    if (!value) return [];
    if (key === "period" && value === "30d") return [];
    if (key === "sort" && value === "criticality:desc") return [];
    const optionLabel = values?.find(([option]) => option === value)?.[1] || value;
    return [`<button type="button" class="ai-filter-chip" data-ai-chip-key="${key}"><span>${escapeHtml(label)}: ${escapeHtml(optionLabel)}</span><b aria-hidden="true">×</b><span class="sr-only">Убрать фильтр</span></button>`];
  });
  if (params.get("includeFixed") === "true") {
    chips.push(`<button type="button" class="ai-filter-chip" data-ai-chip-key="includeFixed"><span>Исправленные включены</span><b aria-hidden="true">×</b><span class="sr-only">Убрать фильтр</span></button>`);
  }
  const resetDisabled = !hasNonDefaultFilters(params, view);
  return `
    <div class="ai-active-filters" aria-label="Активные фильтры">
      <div>${chips.join("")}</div>
      <button class="ghost ai-filter-reset" type="button" data-ai-filter-reset ${resetDisabled ? "disabled" : ""}>Сбросить</button>
    </div>
  `;
}

function renderEvents(overview: AiRopOverview, items: AiRopViolation[], total: number, role: AiRopRole, params: URLSearchParams) {
  const summary = overview.summary || {};
  return `
    ${renderScopeNotice(overview.scope, overview.dataQuality)}
    <section class="ai-rop-summary" aria-label="Сводка событий">
      ${eventSummaryCard("Критические", summary.criticalViolations, "critical", params, { criticality: "critical" })}
      ${eventSummaryCard("Высокий риск", summary.highViolations, "high", params, { criticality: "high" })}
      ${eventSummaryCard("Сделки с риском", summary.dealsWithRisk, "risk", params)}
      ${eventSummaryCard("Новые", summary.newViolations, "new", params, { status: "new" })}
      ${eventSummaryCard("Открытые повторно", summary.reopenedViolations ?? summary.repeatedViolations, "repeated", params, { status: "reopened" })}
    </section>
    <section class="ai-rop-table-panel">
      <header><div><strong>Требует действий</strong><span>${countValue(total)} ${plural(total, "событие", "события", "событий")}${items.length < total ? ` · показано ${items.length}` : ""}</span></div></header>
      ${items.length ? renderTable(items, role) : renderEventsEmpty()}
    </section>
    ${role === "manager" ? "" : renderManagerRating(overview.managerRating || [], params)}
  `;
}

function eventSummaryCard(label: string, value: unknown, tone: string, params: URLSearchParams, filters: Record<string, string> = {}) {
  return `
    <a class="ai-summary-card tone-${tone}" href="${escapeHtml(eventsHref(params, filters))}">
      <span>${escapeHtml(label)}</span><strong>${countValue(value)}</strong>
    </a>
  `;
}

function renderTable(items: AiRopViolation[], role: AiRopRole) {
  return `
    <div class="ai-rop-table-scroll" tabindex="0" aria-label="Таблица событий, доступна горизонтальная прокрутка">
      <table class="ai-rop-table">
        <thead><tr><th>Приоритет</th><th>Сделка</th><th>Ответственный</th><th>Нарушение и доказательство</th><th>Состояние</th><th>Действие</th></tr></thead>
        <tbody>${items.map((item) => `
          <tr class="severity-${escapeHtml(item.criticality)}">
            <td><span class="ai-severity ai-severity-${escapeHtml(item.criticality)}">${severityLabel(item.criticality)}</span></td>
            <td><a class="ai-deal-link" href="${escapeHtml(item.dealUrl)}" target="_blank" rel="noopener noreferrer"><strong>${escapeHtml(item.dealName || `Сделка #${item.dealId}`)}</strong><span>#${item.dealId} · ${escapeHtml(item.stageName)}</span></a></td>
            <td><strong>${escapeHtml(item.responsibleName)}</strong><span class="ai-cell-muted">${escapeHtml(item.pipelineName)}</span></td>
            <td><strong>${escapeHtml(item.title)}</strong><span class="ai-evidence" title="${escapeHtml(item.evidence)}">${escapeHtml(item.evidence)}</span><small>${escapeHtml(item.recommendation)}</small></td>
            <td><span class="ai-status ai-status-${escapeHtml(item.status)}">${statusLabel(item.status)}</span><small>${eventAgeLabel(item)}</small></td>
            <td><div class="ai-row-actions">
              <a class="secondary" href="${escapeHtml(item.dealUrl)}" target="_blank" rel="noopener noreferrer">Открыть amoCRM</a>
              ${item.status === "acknowledged" || item.status === "fixed" ? "" : `<button class="ghost" type="button" data-ai-status="acknowledged" data-ai-key="${escapeHtml(item.stableKey)}">В работу</button>`}
              ${role === "manager" || item.status === "ignored" || item.status === "fixed" ? "" : `<button class="ghost" type="button" data-ai-status="ignored" data-ai-key="${escapeHtml(item.stableKey)}">Исключить</button>`}
            </div></td>
          </tr>
        `).join("")}</tbody>
      </table>
    </div>
  `;
}

function renderManagerRating(rows: AiRopOverview["managerRating"], params: URLSearchParams) {
  if (!rows.length) return "";
  return `
    <section class="ai-rop-managers">
      <header><strong>Нарушения по сотрудникам</strong><span>Не является итоговым рейтингом эффективности</span></header>
      <div>${rows.slice(0, 10).map((row) => `<a href="${escapeHtml(eventsHref(params, row.managerId ? { managerId: String(row.managerId) } : {}))}"><div><strong>${escapeHtml(row.manager)}</strong><span>${countValue(row.critical)} критических · ${countValue(row.high)} высоких</span></div><b>${countValue(row.total)}</b></a>`).join("")}</div>
    </section>
  `;
}

function renderAnalytics(analytics: AiRopAnalytics, params: URLSearchParams) {
  const summary = analytics.summary || {};
  const scope = analytics.dataQuality?.scope as AiRopScope | undefined;
  return `
    ${renderScopeNotice(scope, analytics.dataQuality)}
    ${renderAnalyticsKpis(summary, params)}
    <section class="ai-dashboard-primary">
      ${renderTrendChart(analytics.series || [], params)}
      ${renderSignals(analytics, params)}
    </section>
    <section class="ai-dashboard-split">
      ${renderTypePareto(analytics.byType || [], params)}
      ${renderAging(analytics.aging || [], params)}
    </section>
    ${renderPipelineStageMatrix(analytics.byPipelineStage || {}, params)}
    ${renderEmployees(analytics.employees || [], params)}
    ${renderDataQuality(analytics.dataQuality, analytics.period)}
  `;
}

function renderAnalyticsKpis(summary: Record<string, unknown>, params: URLSearchParams) {
  return `
    <section class="ai-analytics-kpis" aria-label="Ключевые показатели">
      ${analyticsKpi("clean-deals", "Чистые сделки", formatAiRopPercent(summary.cleanDealRate), supportFraction(summary.cleanDeals, summary.eligibleDeals ?? summary.checkedDeals), "Доля применимых проверенных сделок без найденных ошибок.", params)}
      ${analyticsKpi("critical-deals", "Критические сделки", formatAiRopPercent(summary.criticalDealRate), supportFraction(summary.criticalDeals, summary.checkedDeals), "Доля проверенных сделок хотя бы с одной критической ошибкой.", params, { criticality: "critical" }, "critical")}
      ${analyticsKpi("open-backlog", "Открытый остаток", countValue(summary.openBacklog), "Активные события на конец периода", "События, которые еще требуют решения или подтверждения.", params, {}, "risk")}
      ${analyticsKpi("new-incidents", "Новые события", countValue(summary.newIncidents), "За выбранный период", "События, впервые обнаруженные в выбранном периоде.", params, { status: "new" }, "new")}
      ${analyticsKpi("fixed-sla", "Исправлено в SLA", formatAiRopPercent(summary.fixedWithinSla), "В пределах установленного срока", "Доля исправленных событий, закрытых до истечения SLA.", params, { status: "fixed", includeFixed: "true" }, "success")}
      ${analyticsKpi("median-resolution", "Медиана исправления", durationHours(summary.medianResolutionHours), "50% событий исправлены быстрее", "Медианное время от первого обнаружения до исправления.", params, { status: "fixed", includeFixed: "true" })}
    </section>
  `;
}

function analyticsKpi(
  key: string,
  label: string,
  value: string,
  support: string,
  definition: string,
  params: URLSearchParams,
  filters: Record<string, string> = {},
  tone = "neutral"
) {
  const missing = value === "—";
  return `
    <a class="ai-analytics-kpi tone-${tone}${missing ? " is-missing" : ""}" data-ai-kpi="${key}" href="${escapeHtml(eventsHref(params, filters))}" aria-describedby="ai-kpi-help-${key}">
      <span>${escapeHtml(label)}</span>
      <strong>${value}</strong>
      <small>${missing ? "Недостаточно данных" : escapeHtml(support)}</small>
      <span class="sr-only" id="ai-kpi-help-${key}">${escapeHtml(definition)} Открыть связанные события.</span>
    </a>
  `;
}

function renderTrendChart(series: AiRopAnalytics["series"], params: URLSearchParams) {
  const rows = series.filter((item) => typeof item.date === "string" && item.date);
  const hasValues = rows.some((item) => [item.opened, item.fixed, item.backlog].some((value) => numberValue(value) !== null));
  if (!rows.length || !hasValues) {
    return analyticsPanel("Динамика ошибок", "Новые, исправленные и открытый остаток", `<div data-ai-chart="incident-flow">${renderPanelEmpty("Недостаточно данных для построения динамики.")}</div>`, "ai-trend-panel");
  }

  const width = 760;
  const height = 250;
  const left = 42;
  const right = 18;
  const top = 18;
  const bottom = 40;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const allValues = rows.flatMap((item) => [numberValue(item.opened), numberValue(item.fixed), numberValue(item.backlog)]).filter((value): value is number => value !== null);
  const max = Math.max(1, ...allValues);
  const step = chartWidth / Math.max(1, rows.length);
  const barWidth = Math.max(2, Math.min(10, step * 0.28));
  const y = (value: number) => top + chartHeight - (value / max) * chartHeight;
  const labelStep = Math.max(1, Math.ceil(rows.length / 6));
  const backlogPoints: string[] = [];

  const points = rows.map((item, index) => {
    const opened = numberValue(item.opened);
    const fixed = numberValue(item.fixed);
    const backlog = numberValue(item.backlog);
    const x = left + step * index + step / 2;
    if (backlog !== null) backlogPoints.push(`${x.toFixed(1)},${y(backlog).toFixed(1)}`);
    const date = String(item.date);
    const href = eventsHref(params, { period: "custom", from: date, to: date });
    const openedHeight = opened === null ? 0 : chartHeight - (y(opened) - top);
    const fixedHeight = fixed === null ? 0 : chartHeight - (y(fixed) - top);
    return `
      <a href="${escapeHtml(href)}" aria-label="${escapeHtml(`${formatShortDate(date)}: новых ${countValue(opened)}, исправлено ${countValue(fixed)}, остаток ${countValue(backlog)}. Открыть события дня.`)}">
        <rect class="ai-chart-hit" x="${(x - step / 2).toFixed(1)}" y="${top}" width="${step.toFixed(1)}" height="${chartHeight}" />
        ${opened === null ? "" : `<rect class="ai-chart-opened" x="${(x - barWidth - 1).toFixed(1)}" y="${y(opened).toFixed(1)}" width="${barWidth.toFixed(1)}" height="${openedHeight.toFixed(1)}"><title>Новые: ${opened}</title></rect>`}
        ${fixed === null ? "" : `<rect class="ai-chart-fixed" x="${(x + 1).toFixed(1)}" y="${y(fixed).toFixed(1)}" width="${barWidth.toFixed(1)}" height="${fixedHeight.toFixed(1)}"><title>Исправлено: ${fixed}</title></rect>`}
      </a>
      ${index % labelStep === 0 || index === rows.length - 1 ? `<text class="ai-chart-axis-label" x="${x.toFixed(1)}" y="${height - 12}" text-anchor="middle">${escapeHtml(formatChartDate(date))}</text>` : ""}
    `;
  }).join("");

  const grid = [0, .25, .5, .75, 1].map((part) => {
    const gridY = top + chartHeight * (1 - part);
    return `<line class="ai-chart-grid" x1="${left}" y1="${gridY}" x2="${width - right}" y2="${gridY}" /><text class="ai-chart-axis-label" x="${left - 8}" y="${gridY + 4}" text-anchor="end">${integerFormatter.format(max * part)}</text>`;
  }).join("");
  const backlogLine = backlogPoints.length > 1 ? `<polyline class="ai-chart-backlog" points="${backlogPoints.join(" ")}" />` : "";

  return analyticsPanel("Динамика ошибок", "Новые и исправленные события, открытый остаток", `
    <div class="ai-chart-legend" aria-hidden="true"><span class="opened">Новые</span><span class="fixed">Исправлено</span><span class="backlog">Остаток</span></div>
    <div class="ai-chart-scroll">
      <svg class="ai-incident-chart" data-ai-chart="incident-flow" viewBox="0 0 ${width} ${height}" role="img" aria-label="График новых, исправленных и открытых событий по датам. Каждая дата ведет к отфильтрованным событиям.">
        ${grid}${points}${backlogLine}
      </svg>
    </div>
  `, "ai-trend-panel");
}

function renderSignals(analytics: AiRopAnalytics, params: URLSearchParams) {
  const signals: Array<{ title: string; text: string; href: string; tone: string }> = [];
  const topType = maxRow(analytics.byType || [], "activeIncidents", "failed");
  if (topType) signals.push({
    title: "Главный источник ошибок",
    text: `${rowTitle(topType)} · ${countValue(rowNumber(topType, "activeIncidents", "failed"))}`,
    href: eventsHref(params, topType.code ? { code: String(topType.code) } : {}),
    tone: "critical"
  });
  const stages = analytics.byPipelineStage?.stages || [];
  const topStage = maxRow(stages, "failureRate", "activeIncidents");
  if (topStage) signals.push({
    title: "Этап требует внимания",
    text: `${rowTitle(topStage)} · ${formatAiRopPercent(topStage.failureRate)}`,
    href: eventsHref(params, compactFilters({ pipelineId: filterOptionValue(topStage.pipelineId ?? null, String(topStage.pipelineName || "")), stageId: filterOptionValue(topStage.id ?? null, rowTitle(topStage)) })),
    tone: "high"
  });
  const comparableEmployees = (analytics.employees || []).filter(hasComparableAiRopEmployeeSample);
  const topEmployee = maxRow(comparableEmployees, "failureRate", "activeIncidents");
  if (topEmployee) signals.push({
    title: "Максимальная доля ошибок",
    text: `${rowTitle(topEmployee)} · ${formatAiRopPercent(topEmployee.failureRate)}`,
    href: eventsHref(params, topEmployee.id == null ? {} : { managerId: String(topEmployee.id) }),
    tone: "risk"
  });
  const reopened = (analytics.series || []).reduce((sum, item) => sum + (numberValue(item.reopened) || 0), 0);
  if (reopened > 0) signals.push({
    title: "Возвраты ошибок",
    text: `${countValue(reopened)} ${plural(reopened, "событие открыто", "события открыты", "событий открыты")} повторно`,
    href: eventsHref(params, { status: "reopened" }),
    tone: "high"
  });

  const content = signals.length
    ? `<div class="ai-signal-list">${signals.slice(0, 4).map((signal) => `<a class="tone-${signal.tone}" href="${escapeHtml(signal.href)}"><span>${escapeHtml(signal.title)}</span><strong>${escapeHtml(signal.text)}</strong><small>Открыть события →</small></a>`).join("")}</div>`
    : renderPanelEmpty("Сигналы появятся после накопления сопоставимых данных.");
  return analyticsPanel("Сигналы", "Куда руководителю смотреть в первую очередь", content, "ai-signal-panel");
}

function renderTypePareto(rows: AnalyticsRow[], params: URLSearchParams) {
  if (!rows.length) return analyticsPanel("Типы ошибок", "Структура активных событий", renderPanelEmpty("Нет данных по типам ошибок."));
  const values = rows.map((row) => rowNumber(row, "activeIncidents", "failed", "count")).filter((value): value is number => value !== null);
  const max = Math.max(1, ...values);
  const body = `<div class="ai-pareto-list">${rows.slice(0, 10).map((row) => {
    const value = rowNumber(row, "activeIncidents", "failed", "count");
    const width = value === null ? 0 : Math.max(2, (value / max) * 100);
    return `<a href="${escapeHtml(eventsHref(params, row.code ? { code: String(row.code) } : {}))}"><div><strong>${escapeHtml(rowTitle(row))}</strong><span>${formatAiRopPercent(row.failureRate)} от проверенных</span></div><b>${countValue(value)}</b><i style="--bar-width:${width.toFixed(1)}%"></i></a>`;
  }).join("")}</div>`;
  return analyticsPanel("Типы ошибок", "Pareto активных событий", body);
}

function renderAging(rows: AnalyticsRow[], params: URLSearchParams) {
  if (!rows.length) return analyticsPanel("Возраст событий", "Сколько времени ошибки остаются открытыми", renderPanelEmpty("Нет данных о возрасте событий."));
  const values = rows.map((row) => rowNumber(row, "incidents", "activeIncidents", "count")).filter((value): value is number => value !== null);
  const max = Math.max(1, ...values);
  const body = `<div class="ai-aging-list">${rows.map((row) => {
    const bucket = String(row.bucket ?? row.id ?? row.name ?? "");
    const value = rowNumber(row, "incidents", "activeIncidents", "count");
    const width = value === null ? 0 : Math.max(2, (value / max) * 100);
    return `<a href="${escapeHtml(eventsHref(params, bucket ? { age: bucket } : {}))}"><span>${escapeHtml(String(row.label ?? row.name ?? ageLabel(bucket)))}</span><i><b style="width:${width.toFixed(1)}%"></b></i><strong>${countValue(value)}</strong><small>${countValue(row.criticalIncidents)} крит.</small></a>`;
  }).join("")}</div>`;
  return analyticsPanel("Возраст событий", "Сколько времени ошибки остаются открытыми", body);
}

function renderPipelineStageMatrix(data: AiRopAnalytics["byPipelineStage"], params: URLSearchParams) {
  const pipelines = data.pipelines || [];
  const stages = data.stages || [];
  if (!pipelines.length && !stages.length) {
    return analyticsPanel("Воронки и этапы", "Концентрация ошибок в процессе продаж", renderPanelEmpty("Нет данных по воронкам и этапам."));
  }
  const rows: string[] = [];
  pipelines.forEach((pipeline) => {
    rows.push(matrixRow(pipeline, params, "pipeline"));
    stages.filter((stage) => String(stage.pipelineId ?? "") === String(pipeline.id ?? "")).forEach((stage) => rows.push(matrixRow(stage, params, "stage")));
  });
  stages.filter((stage) => !pipelines.some((pipeline) => String(pipeline.id ?? "") === String(stage.pipelineId ?? ""))).forEach((stage) => rows.push(matrixRow(stage, params, "stage")));
  return analyticsPanel("Воронки и этапы", "Концентрация ошибок в процессе продаж", `
    <div class="ai-analytics-table-scroll" tabindex="0" aria-label="Матрица показателей по воронкам и этапам">
      <table class="ai-matrix-table">
        <thead><tr><th>Воронка / этап</th><th>Проверено</th><th>Сделки с ошибками</th><th>Активные</th><th>Критические</th><th>Доля ошибок</th></tr></thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    </div>
  `, "ai-matrix-panel");
}

function matrixRow(row: AnalyticsRow, params: URLSearchParams, kind: "pipeline" | "stage") {
  const filters = kind === "pipeline"
    ? compactFilters({ pipelineId: filterOptionValue(row.id ?? null, rowTitle(row)) })
    : compactFilters({ pipelineId: filterOptionValue(row.pipelineId ?? null, String(row.pipelineName || "")), stageId: filterOptionValue(row.id ?? null, rowTitle(row)) });
  const rate = numberValue(row.failureRate);
  const normalizedRate = rate ?? 0;
  return `
    <tr class="is-${kind}">
      <td><a href="${escapeHtml(eventsHref(params, filters))}">${kind === "stage" ? `<span>${escapeHtml(String(row.pipelineName || ""))}</span>` : ""}<strong>${escapeHtml(rowTitle(row))}</strong></a></td>
      <td>${countValue(row.checkedDeals ?? row.eligible)}</td>
      <td>${countValue(row.affectedDeals ?? row.failed)}</td>
      <td>${countValue(row.activeIncidents)}</td>
      <td>${countValue(row.criticalIncidents)}</td>
      <td><a class="ai-rate-cell" style="--rate:${Math.max(0, Math.min(100, normalizedRate)).toFixed(1)}%" href="${escapeHtml(eventsHref(params, filters))}"><span>${formatAiRopPercent(rate)}</span><i></i></a></td>
    </tr>
  `;
}

function renderEmployees(rows: AnalyticsRow[], params: URLSearchParams) {
  if (!rows.length) return analyticsPanel("Показатели сотрудников", "Сравнение с учетом объема проверенных сделок", renderPanelEmpty("Недостаточно данных для сравнения сотрудников."));
  return analyticsPanel("Показатели сотрудников", "Не является итоговым рейтингом эффективности", `
    <div class="ai-analytics-table-scroll" tabindex="0" aria-label="Таблица показателей сотрудников">
      <table class="ai-employee-table">
        <thead><tr><th>Сотрудник</th><th>Проверено сделок</th><th>Сделки с ошибками</th><th>Активные события</th><th>Критические</th><th>Доля ошибок</th><th>Повторные</th></tr></thead>
        <tbody>${rows.map((row) => {
          const filters: Record<string, string> = row.id == null ? {} : { managerId: String(row.id) };
          return `<tr><td><a href="${escapeHtml(eventsHref(params, filters))}"><strong>${escapeHtml(rowTitle(row))}</strong><span>Открыть события</span></a></td><td>${countValue(row.checkedDeals ?? row.eligible)}</td><td>${countValue(row.affectedDeals ?? row.failed)}</td><td>${countValue(row.activeIncidents)}</td><td>${countValue(row.criticalIncidents)}</td><td>${formatAiRopPercent(row.failureRate)}</td><td>${countValue(row.reopenedIncidents ?? row.repeatedIncidents)}</td></tr>`;
        }).join("")}</tbody>
      </table>
    </div>
  `, "ai-employees-panel");
}

function renderDataQuality(dataQuality: AiRopAnalytics["dataQuality"], period: AiRopAnalytics["period"]) {
  const quality = dataQuality || {};
  const scope = (quality.scope || {}) as AiRopScope;
  const activePipelineIds = arrayValue(scope.activePipelineIds);
  const configuredPipelineIds = arrayValue(scope.configuredPipelineIds);
  const missingPipelineIds = arrayValue(scope.missingPipelineIds);
  const sourceCounts = (quality.sourceCounts || {}) as Record<string, unknown>;
  const hasSourceBreakdown = Object.hasOwn(sourceCounts, "regularLeadsLoaded") || Object.hasOwn(sourceCounts, "unsortedLeadsLoaded");
  const issues = Array.isArray(quality.issues) ? quality.issues.map((issue) => dataQualityIssueLabel(String(issue))).filter(Boolean) : [];
  const warning = quality.latestComplete === false || quality.latestTruncated === true || issues.length > 0;
  const periodLabel = period?.from && period?.to ? `${formatDate(period.from)} — ${formatDate(period.to)}` : "Недостаточно данных";
  return `<details class="ai-report-details"><summary>Данные отчёта</summary>` + analyticsPanel("Данные отчёта", "", `
    <div class="ai-data-quality${warning ? " is-warning" : ""}">
      <div><span>Период</span><strong>${escapeHtml(periodLabel)}</strong></div>
      <div><span>Полнота</span><strong>${escapeHtml(String(quality.completenessLabel || (quality.latestComplete === true ? "Полные данные" : "Недостаточно данных")))}</strong></div>
      <div><span>История</span><strong>${escapeHtml(String(quality.historyLabel || "Недостаточно данных"))}</strong></div>
      <div><span>Сопоставимых отчетов</span><strong>${countValue(quality.canonicalReports ?? period?.canonicalReports)}</strong></div>
      <div><span>Контур воронок</span><strong>${scopeModeLabel(scope, configuredPipelineIds.length, activePipelineIds.length)}</strong></div>
      <div><span>Недоступных воронок</span><strong>${countValue(missingPipelineIds.length)}</strong></div>
      <div><span>Закрытых сделок исключено</span><strong>${scope.closedDealsExcluded == null ? "—" : countValue(scope.closedDealsExcluded)}</strong></div>
      ${hasSourceBreakdown ? `<div><span>Обычных сделок загружено</span><strong>${sourceCounts.regularLeadsLoaded == null ? "—" : countValue(sourceCounts.regularLeadsLoaded)}</strong></div>` : ""}
      ${hasSourceBreakdown ? `<div><span>Неразобранных заявок</span><strong>${sourceCounts.unsortedLeadsLoaded == null ? "—" : countValue(sourceCounts.unsortedLeadsLoaded)}</strong></div>` : ""}
      <div><span>Неполных отчетов</span><strong>${countValue(quality.incompleteReports)}</strong></div>
      <div><span>Дубликатов исключено</span><strong>${countValue(quality.duplicateRunsExcluded)}</strong></div>
      <div><span>Несопоставимых исключено</span><strong>${countValue(quality.incompatibleReportsExcluded)}</strong></div>
    </div>
    ${issues.length ? `<ul class="ai-data-issues">${issues.map((issue) => `<li>${escapeHtml(issue)}</li>`).join("")}</ul>` : ""}
  `, "ai-data-quality-panel") + `</details>`;
}

function renderScopeNotice(_scope: AiRopScope | undefined, dataQuality?: Record<string, unknown>) {
  return dataQuality?.latestComplete === false || dataQuality?.latestTruncated === true
    ? `<p class="ai-data-update-note">Данные отчёта неполные.</p>` : "";
}

function scopeModeLabel(scope: AiRopScope, configuredCount: number, activeCount: number) {
  const mode = String(scope.pipelineMode || scope.mode || "all");
  return mode === "configured" ? `${activeCount} из ${configuredCount || activeCount}` : "Все доступные";
}

function arrayValue(value: unknown): Array<number | string> {
  return Array.isArray(value) ? value.filter((item): item is number | string => typeof item === "number" || typeof item === "string") : [];
}

function analyticsPanel(title: string, subtitle: string, content: string, className = "") {
  return `
    <section class="ai-analytics-panel ${className}">
      <header><div><strong>${escapeHtml(title)}</strong><span>${escapeHtml(subtitle)}</span></div></header>
      ${content}
    </section>
  `;
}

function renderPanelEmpty(message: string) {
  return `<div class="ai-panel-empty"><strong>Недостаточно данных</strong><p>${escapeHtml(message)}</p></div>`;
}

function renderEventsEmpty() {
  return `<section class="ai-rop-empty"><strong>По выбранным условиям событий нет</strong><p>Измените фильтры или дождитесь следующей проверки.</p></section>`;
}

function renderLoadingState() {
  return `
    <div class="ai-workspace-skeleton" aria-label="Загружаем данные ИИ-РОПа">
      <div class="ai-skeleton-header"><span></span><span></span></div>
      <div class="ai-skeleton-filters">${Array.from({ length: 8 }, () => "<span></span>").join("")}</div>
      <div class="ai-rop-loading">${Array.from({ length: 6 }, () => "<span></span>").join("")}</div>
      <div class="ai-skeleton-chart"></div>
    </div>
  `;
}

function renderErrorState(error: unknown) {
  const message = errorMessage(error);
  const permission = /связ(ь|и).+amoCRM|доступ|mapping/i.test(message);
  return `
    <section class="ai-rop-error${permission ? " is-permission" : ""}" role="${permission ? "status" : "alert"}">
      <div><strong>${permission ? "Нет доступа к данным сотрудника" : "Данные ИИ-РОПа временно недоступны"}</strong><p>${escapeHtml(message)}</p></div>
      <button class="secondary" type="button" data-ai-rop-retry>Повторить</button>
    </section>
  `;
}

function bindWorkspace(role: AiRopRole) {
  const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-ai-tab]"));
  tabs.forEach((button, index) => {
    button.addEventListener("click", () => {
      const next = workspaceParams();
      const view = button.dataset.aiTab === "dashboard" ? "dashboard" : "events";
      next.set("view", view);
      if (view === "dashboard") {
        next.delete("q");
        next.delete("sort");
      }
      applyWorkspaceParams(next, role);
    });
    button.addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
      event.preventDefault();
      const targetIndex = event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      requestedTabFocus = tabs[targetIndex]?.dataset.aiTab === "dashboard" ? "dashboard" : "events";
      tabs[targetIndex]?.click();
    });
  });

  document.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-ai-filter]").forEach((input) => {
    if (input.dataset.aiFilter === "q" && input instanceof HTMLInputElement) {
      input.addEventListener("input", () => {
        window.clearTimeout(searchTimer);
        const position = input.selectionStart;
        searchTimer = window.setTimeout(() => applyControls(role, { filter: "q", position }), 350);
      });
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        window.clearTimeout(searchTimer);
        applyControls(role, { filter: "q", position: input.selectionStart });
      });
      return;
    }
    input.addEventListener("change", () => applyControls(role, { filter: input.dataset.aiFilter || "" }));
  });

  document.querySelectorAll<HTMLButtonElement>("[data-ai-chip-key]").forEach((button) => {
    button.addEventListener("click", () => {
      const next = workspaceParams();
      const key = button.dataset.aiChipKey || "";
      next.delete(key);
      if (key === "pipelineId") next.delete("stageId");
      if (key === "period") next.set("period", "30d");
      if (key === "includeFixed" && next.get("status") === "fixed") next.delete("status");
      applyWorkspaceParams(next, role);
    });
  });

  document.querySelector("[data-ai-filter-reset]")?.addEventListener("click", () => {
    const next = new URLSearchParams({ view: currentView(workspaceParams()), period: "30d" });
    applyWorkspaceParams(next, role);
  });

  document.querySelector<HTMLButtonElement>("[data-ai-run]")?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const live = document.querySelector<HTMLElement>("[data-ai-live]");
    button.disabled = true;
    button.textContent = "Проверка запущена";
    if (live) live.textContent = "Внеплановая проверка запущена.";
    try {
      await request("/api/ai-rop/run", { method: "POST", body: "{}" });
      window.clearTimeout(runRefreshTimer);
      runRefreshTimer = window.setTimeout(() => void hydrateAiRop(role), 8000);
    } catch (error) {
      button.disabled = false;
      button.textContent = "Запустить проверку";
      if (live) live.textContent = errorMessage(error);
    }
  });

  document.querySelectorAll<HTMLButtonElement>("[data-ai-status]").forEach((button) => {
    button.addEventListener("click", async () => {
      button.disabled = true;
      try {
        await request(`/api/ai-rop/violations/${encodeURIComponent(button.dataset.aiKey || "")}`, {
          method: "PATCH",
          body: JSON.stringify({ status: button.dataset.aiStatus })
        });
        await hydrateAiRop(role);
      } catch (error) {
        button.disabled = false;
        button.textContent = errorMessage(error);
      }
    });
  });
}

function applyControls(role: AiRopRole, restoreFocus?: FocusRestore) {
  const previous = workspaceParams();
  const next = new URLSearchParams();
  next.set("view", currentView(previous));
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-ai-filter]").forEach((input) => {
    const key = input.dataset.aiFilter || "";
    if (!key) return;
    if (input instanceof HTMLInputElement && input.type === "checkbox") {
      if (input.checked) next.set(key, "true");
      return;
    }
    if (input.value.trim()) next.set(key, input.value.trim());
  });
  if (!next.has("period")) next.set("period", "30d");
  if (next.get("period") === "custom") {
    const panel = document.querySelector<HTMLElement>(".ai-filter-panel");
    if (!next.get("from") && panel?.dataset.aiDateFrom) next.set("from", panel.dataset.aiDateFrom);
    if (!next.get("to") && panel?.dataset.aiDateTo) next.set("to", panel.dataset.aiDateTo);
  } else {
    next.delete("from");
    next.delete("to");
  }
  if (restoreFocus?.filter === "pipelineId") next.delete("stageId");
  if (next.get("status") === "fixed") next.set("includeFixed", "true");
  if (next.get("includeFixed") !== "true" && next.get("status") === "fixed") next.delete("status");
  applyWorkspaceParams(next, role, restoreFocus);
}

function applyWorkspaceParams(params: URLSearchParams, role: AiRopRole, restoreFocus?: FocusRestore) {
  const sorted = new URLSearchParams([...params.entries()].sort(([left], [right]) => left.localeCompare(right)));
  const hash = `#/ai-rop${sorted.size ? `?${sorted.toString()}` : ""}`;
  history.replaceState(null, "", `${location.pathname}${location.search}${hash}`);
  void hydrateAiRop(role, restoreFocus);
}

function restoreFilterFocus(restore?: FocusRestore) {
  if (!restore?.filter) return;
  const input = document.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-ai-filter="${CSS.escape(restore.filter)}"]`);
  if (!input || input.disabled) return;
  focusControl(input);
  if (input instanceof HTMLInputElement && typeof restore.position === "number") input.setSelectionRange(restore.position, restore.position);
}

function workspaceParams() {
  const params = new URLSearchParams(location.hash.split("?")[1] || "");
  if (!params.has("view")) params.set("view", "events");
  if (!params.has("period")) params.set("period", "30d");
  return params;
}

function currentView(params: URLSearchParams): AiRopView {
  return params.get("view") === "dashboard" ? "dashboard" : "events";
}

function queryForFilters(params: URLSearchParams, role: AiRopRole) {
  const query = new URLSearchParams();
  if (role !== "manager" && params.get("managerId")) query.set("managerId", params.get("managerId") || "");
  return query;
}

function queryForData(params: URLSearchParams, view: AiRopView) {
  const query = new URLSearchParams();
  const sharedKeys = ["managerId", "code", "criticality", "status", "from", "to", "age", "includeFixed"];
  sharedKeys.forEach((key) => {
    const value = params.get(key);
    if (value) query.set(key, value);
  });
  appendIdOrNameQuery(query, "pipelineId", "pipeline", params.get("pipelineId"));
  appendIdOrNameQuery(query, "stageId", "stage", params.get("stageId"));
  const period = params.get("period") || "30d";
  if (period !== "custom") query.set("period", period);
  if (view === "events") {
    const q = params.get("q");
    const sort = params.get("sort") || "criticality:desc";
    if (q) query.set("q", q);
    query.set("sort", sort);
    query.set("limit", "100");
  }
  return query;
}

function querySuffix(query: URLSearchParams) {
  return query.size ? `?${query.toString()}` : "";
}

function eventsHref(params: URLSearchParams, additions: Record<string, string> = {}) {
  const next = new URLSearchParams(params);
  next.set("view", "events");
  next.delete("q");
  next.delete("sort");
  Object.entries(additions).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
  return `#/ai-rop?${next.toString()}`;
}

function normalizeFilters(filters: AiRopFilters): AiRopFilters {
  const source = filters || defaultFilters;
  return {
    managers: Array.isArray(source.managers) ? source.managers : [],
    eventTypes: Array.isArray(source.eventTypes) ? source.eventTypes : [],
    pipelines: Array.isArray(source.pipelines) ? source.pipelines : [],
    stages: Array.isArray(source.stages) ? source.stages : [],
    criticalities: Array.isArray(source.criticalities) && source.criticalities.length ? source.criticalities : defaultFilters.criticalities,
    statuses: Array.isArray(source.statuses) && source.statuses.length ? source.statuses : defaultFilters.statuses,
    dateRange: source.dateRange
  };
}

function normalizeValueOptions(
  values: AiRopFilters["criticalities"] | AiRopFilters["statuses"],
  fallbackLabel: (value: string) => string
): Array<[string, string]> {
  return values.flatMap((item) => {
    if (typeof item === "string") return [[item, fallbackLabel(item)] as [string, string]];
    const value = String(item.value ?? item.id ?? "");
    if (!value) return [];
    return [[value, String(item.label ?? item.name ?? fallbackLabel(value))] as [string, string]];
  });
}

function periodOptions(): Array<[string, string]> {
  return [["7d", "7 дней"], ["30d", "30 дней"], ["90d", "90 дней"], ["all", "Вся история"], ["custom", "Произвольный период"]];
}

function ageOptions(): Array<[string, string]> {
  return [["", "Любой возраст"], ["0-1", "До 1 дня"], ["2-3", "2–3 дня"], ["4-7", "4–7 дней"], ["8-14", "8–14 дней"], ["15+", "15+ дней"]];
}

function sortOptions(): Array<[string, string]> {
  return [["criticality:desc", "Сначала критические"], ["-age", "Сначала самые старые"], ["age:asc", "Сначала новые"], ["lastSeenAt:desc", "Недавно обнаруженные"]];
}

function selectOptions(active: string, values: Array<[string, string]>) {
  return values.map(([value, label]) => `<option value="${escapeHtml(value)}" ${value === active ? "selected" : ""}>${escapeHtml(label)}</option>`).join("");
}

function hasNonDefaultFilters(params: URLSearchParams, view: AiRopView) {
  return [...params.entries()].some(([key, value]) => {
    if (key === "view") return value !== view;
    if (key === "period") return value !== "30d";
    if (key === "sort") return view === "events" && value !== "criticality:desc";
    return Boolean(value);
  });
}

function updateCriticalBadges(count: number) {
  document.querySelectorAll<HTMLElement>("[data-ai-rop-badge]").forEach((node) => {
    node.textContent = String(count);
    node.toggleAttribute("hidden", count <= 0);
    node.setAttribute("aria-label", `${count} критических нарушений`);
  });
}

async function request<T = unknown>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { ...init, headers: { "Content-Type": "application/json", ...(init.headers || {}) } });
  const text = await response.text();
  let data: unknown = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = { message: text }; }
  if (!response.ok) {
    const payload = data as { error?: { message?: string }; message?: string };
    if (response.status === 401) {
      window.dispatchEvent(new Event("portal:session-expired"));
      throw new PortalSessionExpiredError("Сессия завершена. Войдите в портал снова.");
    }
    throw new Error(payload?.error?.message || payload?.message || "Не удалось получить данные ИИ-РОПа.");
  }
  return data as T;
}

function compactFilters(values: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== null && value !== undefined && String(value)).map(([key, value]) => [key, String(value)]));
}

function filterOptionValue(id: number | string | null | undefined, name: string) {
  return id === null || id === undefined || id === "" ? (name ? `name:${name}` : "") : String(id);
}

function appendIdOrNameQuery(query: URLSearchParams, idKey: string, nameKey: string, value: string | null) {
  if (!value) return;
  if (value.startsWith("name:")) query.set(nameKey, value.slice(5));
  else query.set(idKey, value);
}

function maxRow(rows: AnalyticsRow[], primary: string, secondary: string) {
  return rows.reduce<AnalyticsRow | null>((best, row) => {
    const score = rowNumber(row, primary, secondary);
    if (score === null) return best;
    const bestScore = best ? rowNumber(best, primary, secondary) : null;
    return bestScore === null || score > bestScore ? row : best;
  }, null);
}

function rowNumber(row: AnalyticsRow, ...keys: string[]) {
  for (const key of keys) {
    const value = numberValue(row[key]);
    if (value !== null) return value;
  }
  return null;
}

function rowTitle(row: AnalyticsRow) {
  return String(row.title ?? row.name ?? row.code ?? row.id ?? "Без названия");
}

function numberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function countValue(value: unknown) {
  const parsed = numberValue(value);
  return parsed === null ? "—" : integerFormatter.format(parsed);
}

export function formatAiRopPercent(value: unknown) {
  const parsed = numberValue(value);
  if (parsed === null) return "—";
  return `${numberFormatter.format(parsed)}%`;
}

export function hasComparableAiRopEmployeeSample(row: Record<string, unknown>) {
  return (numberValue(row.checkedDeals) || 0) >= 10 && (numberValue(row.eligible) || 0) >= 30;
}

function durationHours(value: unknown) {
  const parsed = numberValue(value);
  if (parsed === null) return "—";
  if (parsed < 24) return `${numberFormatter.format(parsed)} ч`;
  return `${numberFormatter.format(parsed / 24)} дн`;
}

function supportFraction(numerator: unknown, denominator: unknown) {
  const left = numberValue(numerator);
  const right = numberValue(denominator);
  return left === null || right === null ? "Недостаточно данных" : `${integerFormatter.format(left)} из ${integerFormatter.format(right)} проверенных`;
}

function eventAgeLabel(item: AiRopViolation) {
  const age = numberValue(item.ageDays);
  if (age !== null) return age < 1 ? "Менее суток" : `${numberFormatter.format(age)} ${plural(age, "день", "дня", "дней")}`;
  return item.occurrenceCount > 1 ? `Повторений: ${item.occurrenceCount}` : `С ${formatDate(item.firstSeenAt)}`;
}

function ageLabel(value: string) {
  return ageOptions().find(([key]) => key === value)?.[1] || value || "Не определено";
}

function dataQualityIssueLabel(value: string) {
  return ({
    lead_limit_reached: "Достигнут лимит загрузки сделок: часть воронки могла не попасть в расчет.",
    stage_events_unavailable: "История смены этапов недоступна для части сделок.",
    unsorted_leads_unavailable: "Неразобранные заявки amoCRM недоступны: прогон считается неполным, автозакрытие событий отключено.",
    legacy_reports_limited: "Старые отчеты содержат ограниченный набор показателей.",
    history_unavailable: "Недостаточно исторических отчетов для надежной динамики.",
    stage_timing_fallback_used: "Для части сделок время на этапе рассчитано по резервной дате обновления.",
    rule_evaluation_errors: "Часть правил завершилась ошибкой и не включена в расчет.",
    incompatible_reports_excluded: "Отчеты с другой областью проверки или версией метрик исключены из динамики.",
    configured_pipeline_missing: "Часть настроенных воронок больше не найдена в amoCRM и пропущена без остановки проверки.",
    configured_pipelines_unavailable: "Ни одна настроенная воронка не найдена: проверка завершена без загрузки чужих сделок.",
    ai_comment_evaluation_disabled: "AI-проверка содержательности комментариев не настроена; связанные критерии не включены в показатели."
  } as Record<string, string>)[value] || "Обнаружено дополнительное ограничение качества данных.";
}

function severityLabel(value: string) {
  return value === "critical" ? "Критично" : value === "high" ? "Высокая" : value === "medium" ? "Средняя" : value;
}

function statusLabel(value: string) {
  return ({ new: "Новое", open: "Открыто", reopened: "Переоткрыто", repeated: "Повторное", acknowledged: "В работе", ignored: "Исключение", fixed: "Исправлено", out_of_scope: "Вне контура" } as Record<string, string>)[value] || value;
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : new Intl.DateTimeFormat("ru-RU").format(date);
}

function formatShortDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(date);
}

function formatChartDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.slice(0, 5) : new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit" }).format(date);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "Нет данных";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Нет данных" : new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function plural(value: number, one: string, few: string, many: string) {
  const integer = Math.round(value);
  const mod100 = integer % 100;
  const mod10 = integer % 10;
  return mod100 >= 11 && mod100 <= 19 ? many : mod10 === 1 ? one : mod10 >= 2 && mod10 <= 4 ? few : many;
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Неизвестная ошибка";
}

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

function escapeHtml(value: unknown) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] || char);
}
